import { useEffect, useMemo, useState } from 'react'
import { CalendarDaysIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import DateInput from '@/components/DateInput'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import WarningBanner from '@/components/WarningBanner'
import { useQBOData } from '@/hooks/useQBOData'
import {
  createFixedAsset,
  listFixedAssetQboAccounts,
  updateFixedAsset,
  type ExpenseType,
  type FixedAssetListItem,
  type FixedAssetWriteBody,
  type MonthSummaryItem,
} from '@/services/fixedAssets'
import { toast } from '@/store/toastStore'
import { HoverHelp } from '@/tableControls'
import { formatApiError } from '@/utils/apiError'
import {
  ASSET_CLASSES,
  LOCATIONS,
  type AssetClass,
} from './fixedAssetConstants'
import { formatMoneyInput, parseMoney } from './fixedAssetsFormat'

const CLASS_TOOLTIP =
  'The Chart of Accounts category this asset belongs to — matching the QBO parent account, and analogous to QBO’s Fixed Asset or Other Asset detail type (Buildings, Furniture and Fixtures, Vehicles, Startup Costs, Goodwill, etc.). This is not QBO Class tracking; use Location for store or department.'

export type FixedAssetModalPrefill = Partial<FixedAssetWriteBody> & {
  id?: number
}

interface FixedAssetModalProps {
  open: boolean
  onClose: () => void
  onSaved: (createdId?: number) => void
  asset?: FixedAssetListItem | null
  prefill?: FixedAssetModalPrefill
  monthsSummary: MonthSummaryItem[]
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function emptyForm(): FixedAssetWriteBody {
  const today = todayIso()
  return {
    name: '',
    asset_class: '',
    location: 'Highland Park',
    description: null,
    purchase_price: '',
    salvage_value: formatMoneyInput(0),
    purchase_date: today,
    dep_start_date: today,
    method: 'straight_line',
    useful_life_years: 5,
    non_depreciable: false,
    expense_type: 'depreciation',
    asset_account_qbo_id: '',
    expense_account_qbo_id: '',
    accumulated_account_qbo_id: '',
  }
}

function formFromAsset(asset: FixedAssetListItem): FixedAssetWriteBody {
  return {
    name: asset.name,
    asset_class: asset.asset_class,
    location: asset.location,
    description: asset.description,
    purchase_price: formatMoneyInput(asset.purchase_price),
    salvage_value: formatMoneyInput(asset.salvage_value),
    purchase_date: asset.purchase_date.slice(0, 10),
    dep_start_date: asset.dep_start_date.slice(0, 10),
    method: asset.method,
    useful_life_years: asset.useful_life_years,
    non_depreciable: asset.non_depreciable,
    expense_type: asset.expense_type,
    asset_account_qbo_id: asset.asset_account_qbo_id,
    expense_account_qbo_id: asset.expense_account_qbo_id,
    accumulated_account_qbo_id: asset.accumulated_account_qbo_id,
  }
}

function formFromPrefill(prefill: FixedAssetModalPrefill): FixedAssetWriteBody {
  const base = emptyForm()
  return {
    ...base,
    ...prefill,
    purchase_date: prefill.purchase_date?.slice(0, 10) ?? base.purchase_date,
    dep_start_date: prefill.dep_start_date?.slice(0, 10) ?? base.dep_start_date,
    purchase_price:
      prefill.purchase_price == null || prefill.purchase_price === ''
        ? base.purchase_price
        : formatMoneyInput(prefill.purchase_price),
    salvage_value:
      prefill.salvage_value == null || prefill.salvage_value === ''
        ? base.salvage_value
        : formatMoneyInput(prefill.salvage_value),
    description: prefill.description ?? base.description,
    method: prefill.method ?? base.method,
    non_depreciable: prefill.non_depreciable ?? base.non_depreciable,
    useful_life_years: prefill.useful_life_years ?? base.useful_life_years,
    expense_type: prefill.expense_type ?? base.expense_type,
  }
}

export default function FixedAssetModal({
  open,
  onClose,
  onSaved,
  asset,
  prefill,
  monthsSummary,
}: FixedAssetModalProps) {
  const { categories } = useQBOData()
  const [form, setForm] = useState<FixedAssetWriteBody>(emptyForm())
  const [qboAccounts, setQboAccounts] = useState<Awaited<ReturnType<typeof listFixedAssetQboAccounts>>>([])
  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const editingId = asset?.id ?? prefill?.id
  const hasPostedMonths = monthsSummary.some((month) => month.je_status === 'posted')

  useEffect(() => {
    if (!open) return
    if (asset) {
      setForm(formFromAsset(asset))
    } else if (prefill) {
      setForm(formFromPrefill(prefill))
    } else {
      setForm(emptyForm())
    }
    setErrors({})
  }, [open, asset, prefill])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadingAccounts(true)
    listFixedAssetQboAccounts()
      .then((accounts) => {
        if (!cancelled) setQboAccounts(accounts)
      })
      .catch((err) => {
        console.error(err)
        toast.error('Failed to load QuickBooks accounts')
      })
      .finally(() => {
        if (!cancelled) setLoadingAccounts(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const locationOptions: SelectOption[] = LOCATIONS.map((location) => ({
    id: location,
    label: location,
  }))

  const assetClassOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = ASSET_CLASSES.map((assetClass) => ({
      id: assetClass,
      label: assetClass,
    }))
    const current = form.asset_class
    if (current && !ASSET_CLASSES.includes(current as AssetClass)) {
      options.unshift({ id: current, label: current })
    }
    return options
  }, [form.asset_class])

  const assetAccountOptions: SelectOption[] = useMemo(
    () =>
      qboAccounts.map((account) => ({
        id: account.id,
        label: account.fully_qualified_name || account.name,
        disabled:
          account.already_linked &&
          account.id !== form.asset_account_qbo_id &&
          account.id !== asset?.asset_account_qbo_id,
      })),
    [qboAccounts, form.asset_account_qbo_id, asset?.asset_account_qbo_id],
  )

  const accumAccountOptions: SelectOption[] = useMemo(
    () =>
      qboAccounts.map((account) => ({
        id: account.id,
        label: account.fully_qualified_name || account.name,
      })),
    [qboAccounts],
  )

  const expenseAccountOptions: SelectOption[] = useMemo(
    () =>
      categories
        .filter((category) => category.active)
        .map((category) => ({
          id: category.id,
          label: category.name,
        })),
    [categories],
  )

  function validate(): Record<string, string> {
    const next: Record<string, string> = {}
    const price = parseMoney(form.purchase_price)
    const salvage = parseMoney(form.salvage_value)

    if (!form.name.trim()) next.name = 'Name is required'
    if (!form.asset_class.trim()) next.asset_class = 'Class is required'
    if (price <= 0) next.purchase_price = 'Purchase price must be greater than zero'
    if (salvage < 0) next.salvage_value = 'Salvage must be zero or positive'
    if (salvage >= price) next.salvage_value = 'Salvage must be less than purchase price'
    if (!form.non_depreciable && form.useful_life_years < 1) {
      next.useful_life_years = 'Useful life must be at least 1 year'
    }
    if (form.dep_start_date < form.purchase_date) {
      next.dep_start_date = 'In-service date must be on or after purchase date'
    }
    if (!form.asset_account_qbo_id) next.asset_account_qbo_id = 'Asset account is required'
    if (!form.expense_account_qbo_id) next.expense_account_qbo_id = 'Expense account is required'
    if (!form.accumulated_account_qbo_id) {
      next.accumulated_account_qbo_id = 'Accumulated account is required'
    }

    return next
  }

  async function handleSave() {
    const validation = validate()
    setErrors(validation)
    if (Object.keys(validation).length > 0) return

    setSaving(true)
    try {
      const body: FixedAssetWriteBody = {
        ...form,
        name: form.name.trim(),
        asset_class: form.asset_class.trim(),
        purchase_price: String(parseMoney(form.purchase_price)),
        salvage_value: String(parseMoney(form.salvage_value)),
        method: 'straight_line',
      }

      if (editingId) {
        await updateFixedAsset(editingId, body)
        toast.success('Asset updated.')
        onSaved()
      } else {
        const created = await createFixedAsset(body)
        toast.success('Asset created.')
        onSaved(created.id)
      }
      onClose()
    } catch (err) {
      console.error(err)
      toast.error(formatApiError(err, 'Failed to save asset'))
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  const title = editingId ? 'Edit asset' : 'Add asset'

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div
        className="max-h-[95vh] w-full min-w-0 max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fixed-asset-modal-title"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-gray-100 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <h3 id="fixed-asset-modal-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
          <Button
            variant="outline"
            iconOnly
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-6 sm:py-5">
          {editingId && hasPostedMonths ? (
            <WarningBanner
              message="Past months are not rewritten — the next generated JE will include a true-up."
            />
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-gray-700">Name</span>
              <input
                type="text"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              {errors.name ? <p className="mt-1 text-xs text-red-600">{errors.name}</p> : null}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                <HoverHelp label="Class" aria-label="Fixed asset class help">
                  {CLASS_TOOLTIP}
                </HoverHelp>
              </span>
              <SearchableSelect
                className="mt-1"
                options={assetClassOptions}
                value={form.asset_class || null}
                onChange={(id) =>
                  setForm((prev) => ({ ...prev, asset_class: id ?? '' }))
                }
                preserveOrder
                placeholder="Select class"
              />
              {errors.asset_class ? (
                <p className="mt-1 text-xs text-red-600">{errors.asset_class}</p>
              ) : null}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Location</span>
              <SearchableSelect
                className="mt-1"
                options={locationOptions}
                value={form.location}
                onChange={(id) =>
                  setForm((prev) => ({ ...prev, location: id ?? 'Highland Park' }))
                }
                preserveOrder
              />
            </label>

            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-gray-700">Description</span>
              <textarea
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                rows={2}
                value={form.description ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    description: e.target.value || null,
                  }))
                }
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Purchase price</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="$0.00"
                className="input mt-1 tabular-nums"
                value={form.purchase_price}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, purchase_price: e.target.value }))
                }
                onFocus={(e) => e.currentTarget.select()}
                onBlur={() =>
                  setForm((prev) => ({
                    ...prev,
                    purchase_price: formatMoneyInput(prev.purchase_price),
                  }))
                }
              />
              {errors.purchase_price ? (
                <p className="mt-1 text-xs text-red-600">{errors.purchase_price}</p>
              ) : null}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Salvage value</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="$0.00"
                className="input mt-1 tabular-nums"
                value={form.salvage_value}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, salvage_value: e.target.value }))
                }
                onFocus={(e) => e.currentTarget.select()}
                onBlur={() =>
                  setForm((prev) => ({
                    ...prev,
                    salvage_value: formatMoneyInput(prev.salvage_value),
                  }))
                }
              />
              {errors.salvage_value ? (
                <p className="mt-1 text-xs text-red-600">{errors.salvage_value}</p>
              ) : null}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Purchase date</span>
              <div className="relative mt-1">
                <DateInput
                  className="input pr-9"
                  dateFormat="us"
                  placeholder="mm/dd/yyyy"
                  value={form.purchase_date}
                  onChange={(value) => setForm((prev) => ({ ...prev, purchase_date: value }))}
                />
                <CalendarDaysIcon
                  className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">In-service date</span>
              <div className="relative mt-1">
                <DateInput
                  className="input pr-9"
                  dateFormat="iso"
                  placeholder="yyyy-mm-dd"
                  value={form.dep_start_date}
                  onChange={(value) => setForm((prev) => ({ ...prev, dep_start_date: value }))}
                />
                <CalendarDaysIcon
                  className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              {errors.dep_start_date ? (
                <p className="mt-1 text-xs text-red-600">{errors.dep_start_date}</p>
              ) : null}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Method</span>
              <input
                type="text"
                className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                value="Straight line"
                readOnly
                disabled
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Useful life (years)</span>
              <input
                type="number"
                min={1}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm tabular-nums"
                value={form.useful_life_years}
                disabled={form.non_depreciable}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    useful_life_years: Number(e.target.value) || 0,
                  }))
                }
              />
              {errors.useful_life_years ? (
                <p className="mt-1 text-xs text-red-600">{errors.useful_life_years}</p>
              ) : null}
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Expense type</span>
              <select
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={form.expense_type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    expense_type: e.target.value as ExpenseType,
                  }))
                }
              >
                <option value="depreciation">Depreciation</option>
                <option value="amortization">Amortization</option>
              </select>
            </label>

            <label className="flex items-center gap-2 sm:col-span-2">
              <input
                type="checkbox"
                checked={form.non_depreciable}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    non_depreciable: e.target.checked,
                    useful_life_years: e.target.checked ? prev.useful_life_years : prev.useful_life_years || 5,
                  }))
                }
              />
              <span className="text-sm text-gray-700">Non-depreciable</span>
            </label>

            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-gray-700">Asset account</span>
              <SearchableSelect
                className="mt-1"
                options={assetAccountOptions}
                value={form.asset_account_qbo_id || null}
                onChange={(id) =>
                  setForm((prev) => ({ ...prev, asset_account_qbo_id: id ?? '' }))
                }
                disabled={loadingAccounts}
                placeholder="Select asset account"
              />
              {errors.asset_account_qbo_id ? (
                <p className="mt-1 text-xs text-red-600">{errors.asset_account_qbo_id}</p>
              ) : null}
            </label>

            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-gray-700">Expense account</span>
              <SearchableSelect
                className="mt-1"
                options={expenseAccountOptions}
                value={form.expense_account_qbo_id || null}
                onChange={(id) =>
                  setForm((prev) => ({ ...prev, expense_account_qbo_id: id ?? '' }))
                }
                placeholder="Select expense account"
              />
              {errors.expense_account_qbo_id ? (
                <p className="mt-1 text-xs text-red-600">{errors.expense_account_qbo_id}</p>
              ) : null}
            </label>

            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-gray-700">Accumulated account</span>
              <SearchableSelect
                className="mt-1"
                options={accumAccountOptions}
                value={form.accumulated_account_qbo_id || null}
                onChange={(id) =>
                  setForm((prev) => ({ ...prev, accumulated_account_qbo_id: id ?? '' }))
                }
                disabled={loadingAccounts}
                placeholder="Select accumulated account"
              />
              {errors.accumulated_account_qbo_id ? (
                <p className="mt-1 text-xs text-red-600">{errors.accumulated_account_qbo_id}</p>
              ) : null}
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3 sm:px-6 sm:py-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
